/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Feature Flags - Features Domain
 *
 * Feature flags and configuration settings for the application.
 * Used for:
 * - Feature toggling and A/B testing
 * - Progressive feature rollout
 * - Environment-specific features
 * - Subscription-based feature access
 */

// ============================================================================
// FEATURE FLAGS - AUTHORITATIVE SOURCE
// ============================================================================

export const FEATURE_FLAGS = {
  // Core Features - AI capabilities
  CORE: {
    AI_PROPOSAL_GENERATION: "ai_proposal_generation", // Starter+
    AI_ESTIMATE_GENERATION: "ai_estimate_generation", // Starter+
    AI_DOCUMENT_ANALYSIS: "ai_document_analysis", // Mobile+ (base)
    AI_CHAT_ASSISTANT: "ai_chat_assistant", // Mobile+ (base)
    AI_PRICING_OPTIMIZATION: "ai_pricing_optimization", // Professional+
    AI_COST_ANALYSIS: "ai_cost_analysis", // Professional+ (used in routes)
    AI_TAKEOFF_ANALYSIS: "ai_takeoff_analysis", // Business+ (blueprint analysis)
    ENABLE_ENHANCED_TAKEOFF: "enable_enhanced_takeoff", // Starter+
  },

  // Document Management
  DOCUMENTS: {
    BASIC_DOCUMENTS: "basic_documents", // Mobile+ (basic read/upload)
    ADVANCED_OCR: "advanced_ocr", // Business+
    PDF_ANNOTATION: "pdf_annotation", // Starter+
    DOCUMENT_COMPARISON: "document_comparison", // Business+
    VERSION_CONTROL: "version_control", // Starter+
    BULK_UPLOAD: "bulk_upload", // Mobile+ (base)
    DOCUMENT_TEMPLATES: "document_templates", // Mobile+ (base)
  },

  // Communication Features
  COMMUNICATION: {
    EMAIL_INTEGRATION: "email_integration", // Starter+
    SMS_NOTIFICATIONS: "sms_notifications", // Professional+
    PUSH_NOTIFICATIONS: "push_notifications", // Mobile+ (base)
    IN_APP_MESSAGING: "in_app_messaging", // Mobile+ (base)
    COMMUNICATION_TEMPLATES: "communication_templates", // Starter+
    AUTOMATED_FOLLOW_UPS: "automated_follow_ups", // Professional+
  },

  // Integration Features
  INTEGRATIONS: {
    QUICKBOOKS_INTEGRATION: "quickbooks_integration", // Professional+
    SALESFORCE_INTEGRATION: "salesforce_integration", // Business+
    HUBSPOT_INTEGRATION: "hubspot_integration", // Business+
    STRIPE_INTEGRATION: "stripe_integration", // Mobile+ (base)
    GOOGLE_WORKSPACE_INTEGRATION: "google_workspace_integration", // Professional+
    MICROSOFT_365_INTEGRATION: "microsoft_365_integration", // Professional+
    ZAPIER_INTEGRATION: "zapier_integration", // Business+
    WEBHOOK_SUPPORT: "webhook_support", // Business+
  },

  // Analytics & Reporting
  ANALYTICS: {
    BASIC_ANALYTICS: "basic_analytics", // Mobile+ (base)
    ADVANCED_ANALYTICS: "advanced_analytics", // Professional+
    CUSTOM_REPORTS: "custom_reports", // Professional+
    DATA_EXPORT: "data_export", // Starter+
    PERFORMANCE_DASHBOARDS: "performance_dashboards", // Professional+
    PREDICTIVE_ANALYTICS: "predictive_analytics", // Business+
    COMPETITIVE_ANALYSIS: "competitive_analysis", // Business+
  },

  // Collaboration Features
  COLLABORATION: {
    TEAM_COLLABORATION: "team_collaboration", // Professional+
    REAL_TIME_EDITING: "real_time_editing", // Business+
    COMMENTS_AND_FEEDBACK: "comments_and_feedback", // Professional+
    APPROVAL_WORKFLOWS: "approval_workflows", // Business+
    TASK_MANAGEMENT: "task_management", // Professional+
    PROJECT_TASK_MANAGEMENT: "project_task_management", // Professional+ (used in routes)
    PROJECT_TIMELINES: "project_timelines", // Business+
  },

  // Organization Features
  ORGANIZATION: {
    DIVISIONS: "divisions", // Business+ (multi-location management)
    MULTI_LOCATION: "multi_location", // Business+ (per pricing page)
    LABOR_RESOURCES: "labor_resources", // Starter+ (crew management)
    TEMPLATES_MANAGEMENT: "templates_management", // Starter+
    BUDGETS: "budgets", // Professional+ (budget-driven pricing)
  },

  // Mobile Features
  MOBILE: {
    MOBILE_APP: "mobile_app", // Mobile+ (base)
    OFFLINE_MODE: "offline_mode", // Professional+ — React Query persist + mutation queue. Revisit Starter/Mobile post-launch.
    CAMERA_INTEGRATION: "camera_integration", // Mobile+ (base)
    GPS_LOCATION: "gps_location", // Professional+ (jobsite tracking)
    PUSH_NOTIFICATIONS: "mobile_push_notifications", // Mobile+ (base)
    VOICE_NOTES: "voice_notes", // Mobile+ (base, core Mobile tier value)
  },

  // Security Features
  SECURITY: {
    ADVANCED_AUTHENTICATION: "advanced_authentication", // Professional+
    SSO_INTEGRATION: "sso_integration", // Business+
    AUDIT_LOGGING: "audit_logging", // Business+
    DATA_ENCRYPTION: "data_encryption", // Professional+
    COMPLIANCE_REPORTING: "compliance_reporting", // Business+
    PENETRATION_TESTING: "penetration_testing", // Business+
  },

  // Advanced Features
  ADVANCED: {
    MULTI_TENANCY: "multi_tenancy", // Business+
    WHITE_LABEL: "white_label", // Business+
    API_ACCESS: "api_access", // Business+
    CUSTOM_BRANDING: "custom_branding", // Professional+
    ADVANCED_PERMISSIONS: "advanced_permissions", // Business+
    WORKFLOW_AUTOMATION: "workflow_automation", // Business+
    MODEL_SELECTION: "model_selection", // Enterprise only (not yet released)
  },

  // Field Operations Features
  OPERATIONS: {
    TIME_TRACKING: "time_tracking", // Starter+
    CREW_MANAGEMENT: "crew_management", // Professional+
    RESOURCE_ASSIGNMENT: "resource_assignment", // Professional+
    RECURRING_SERVICES: "recurring_services", // Starter+
    ROUTE_OPTIMIZATION: "route_optimization", // Professional+
    FIELD_VIEW: "field_view", // Mobile+ (core value prop)
  },
} as const;

// ============================================================================
// FEATURE TIERS - SUBSCRIPTION-BASED ACCESS
// Maps to subscription plans: TRIAL→STARTER, MOBILE, STARTER, PROFESSIONAL, BUSINESS
// ============================================================================

export const FEATURE_TIERS = {
  MOBILE: "mobile",
  STARTER: "starter",
  PROFESSIONAL: "professional",
  BUSINESS: "business",
  ENTERPRISE: "enterprise", // Reserved for future plans
} as const;

export type FeatureTier = (typeof FEATURE_TIERS)[keyof typeof FEATURE_TIERS];

// ============================================================================
// TIER FEATURE DEFINITIONS
// Mobile < Starter < Professional < Business (each tier includes all lower tiers)
// ============================================================================

// Base features available to all tiers (including Mobile)
// These are the core value props for the $29/mo Mobile tier
const BASE_FEATURES: string[] = [
  // AI - Basic assistant and document analysis
  FEATURE_FLAGS.CORE.AI_CHAT_ASSISTANT,
  FEATURE_FLAGS.CORE.AI_DOCUMENT_ANALYSIS,
  // Documents - Basic management
  FEATURE_FLAGS.DOCUMENTS.BASIC_DOCUMENTS,
  FEATURE_FLAGS.DOCUMENTS.BULK_UPLOAD,
  FEATURE_FLAGS.DOCUMENTS.DOCUMENT_TEMPLATES,
  // Mobile - Core mobile features (the Mobile tier's value prop)
  FEATURE_FLAGS.MOBILE.MOBILE_APP,
  FEATURE_FLAGS.MOBILE.CAMERA_INTEGRATION,
  FEATURE_FLAGS.MOBILE.VOICE_NOTES,
  FEATURE_FLAGS.MOBILE.PUSH_NOTIFICATIONS,
  // Communication - Basic notifications
  FEATURE_FLAGS.COMMUNICATION.PUSH_NOTIFICATIONS,
  FEATURE_FLAGS.COMMUNICATION.IN_APP_MESSAGING,
  // Integrations - Stripe only
  FEATURE_FLAGS.INTEGRATIONS.STRIPE_INTEGRATION,
  // Analytics - Basic only
  FEATURE_FLAGS.ANALYTICS.BASIC_ANALYTICS,
  // Operations - Field view (Mobile tier's operations value)
  FEATURE_FLAGS.OPERATIONS.FIELD_VIEW,
];

// Starter tier features ($79/mo) - Full platform for solo contractors
// Includes: base + full AI capabilities + document management
const STARTER_FEATURES: string[] = [
  ...BASE_FEATURES,
  // AI - Full AI capabilities
  FEATURE_FLAGS.CORE.AI_PROPOSAL_GENERATION,
  FEATURE_FLAGS.CORE.AI_ESTIMATE_GENERATION,
  FEATURE_FLAGS.CORE.ENABLE_ENHANCED_TAKEOFF,
  // Documents - Advanced document management
  FEATURE_FLAGS.DOCUMENTS.PDF_ANNOTATION,
  FEATURE_FLAGS.DOCUMENTS.VERSION_CONTROL,
  // Communication - Email integration
  FEATURE_FLAGS.COMMUNICATION.EMAIL_INTEGRATION,
  FEATURE_FLAGS.COMMUNICATION.COMMUNICATION_TEMPLATES,
  // Analytics - Data export
  FEATURE_FLAGS.ANALYTICS.DATA_EXPORT,
  // Organization - Basic management
  FEATURE_FLAGS.ORGANIZATION.LABOR_RESOURCES,
  FEATURE_FLAGS.ORGANIZATION.TEMPLATES_MANAGEMENT,
  // Operations - Time tracking + recurring services
  FEATURE_FLAGS.OPERATIONS.TIME_TRACKING,
  FEATURE_FLAGS.OPERATIONS.RECURRING_SERVICES,
];

// Professional tier features ($149/mo) - Growing teams with QuickBooks
// Includes: starter + integrations + team collaboration + advanced AI
const PROFESSIONAL_FEATURES: string[] = [
  ...STARTER_FEATURES,
  // AI - Advanced analysis
  FEATURE_FLAGS.CORE.AI_PRICING_OPTIMIZATION,
  FEATURE_FLAGS.CORE.AI_COST_ANALYSIS,
  // Integrations - QuickBooks & workspace integrations
  FEATURE_FLAGS.INTEGRATIONS.QUICKBOOKS_INTEGRATION,
  FEATURE_FLAGS.INTEGRATIONS.GOOGLE_WORKSPACE_INTEGRATION,
  FEATURE_FLAGS.INTEGRATIONS.MICROSOFT_365_INTEGRATION,
  // Analytics - Advanced dashboards and reports
  FEATURE_FLAGS.ANALYTICS.ADVANCED_ANALYTICS,
  FEATURE_FLAGS.ANALYTICS.CUSTOM_REPORTS,
  FEATURE_FLAGS.ANALYTICS.PERFORMANCE_DASHBOARDS,
  // Collaboration - Team features
  FEATURE_FLAGS.COLLABORATION.TEAM_COLLABORATION,
  FEATURE_FLAGS.COLLABORATION.COMMENTS_AND_FEEDBACK,
  FEATURE_FLAGS.COLLABORATION.TASK_MANAGEMENT,
  FEATURE_FLAGS.COLLABORATION.PROJECT_TASK_MANAGEMENT,
  // Mobile - GPS tracking
  FEATURE_FLAGS.MOBILE.GPS_LOCATION,
  // Communication - SMS & automation
  FEATURE_FLAGS.COMMUNICATION.SMS_NOTIFICATIONS,
  FEATURE_FLAGS.COMMUNICATION.AUTOMATED_FOLLOW_UPS,
  // Security - Enhanced security
  FEATURE_FLAGS.SECURITY.ADVANCED_AUTHENTICATION,
  FEATURE_FLAGS.SECURITY.DATA_ENCRYPTION,
  // Advanced - Custom branding
  FEATURE_FLAGS.ADVANCED.CUSTOM_BRANDING,
  // Organization - Budget-driven pricing
  FEATURE_FLAGS.ORGANIZATION.BUDGETS,
  // Operations - Crews, resource assignment, route optimization
  FEATURE_FLAGS.OPERATIONS.CREW_MANAGEMENT,
  FEATURE_FLAGS.OPERATIONS.RESOURCE_ASSIGNMENT,
  FEATURE_FLAGS.OPERATIONS.ROUTE_OPTIMIZATION,
  // Mobile - Offline mode (Pro+ per locked tier matrix v3, 2026-04-13)
  FEATURE_FLAGS.MOBILE.OFFLINE_MODE,
];

// Business tier features ($299/mo) - Multi-location with API access
// Includes: professional + API + multi-location + advanced integrations
const BUSINESS_FEATURES: string[] = [
  ...PROFESSIONAL_FEATURES,
  // AI - Blueprint analysis
  FEATURE_FLAGS.CORE.AI_TAKEOFF_ANALYSIS,
  // Documents - Advanced OCR & comparison
  FEATURE_FLAGS.DOCUMENTS.ADVANCED_OCR,
  FEATURE_FLAGS.DOCUMENTS.DOCUMENT_COMPARISON,
  // Organization - Multi-location / Divisions
  FEATURE_FLAGS.ORGANIZATION.DIVISIONS,
  FEATURE_FLAGS.ORGANIZATION.MULTI_LOCATION,
  // Integrations - API & webhooks
  FEATURE_FLAGS.INTEGRATIONS.ZAPIER_INTEGRATION,
  FEATURE_FLAGS.INTEGRATIONS.WEBHOOK_SUPPORT,
  FEATURE_FLAGS.INTEGRATIONS.SALESFORCE_INTEGRATION,
  FEATURE_FLAGS.INTEGRATIONS.HUBSPOT_INTEGRATION,
  // Analytics - Predictive & competitive
  FEATURE_FLAGS.ANALYTICS.PREDICTIVE_ANALYTICS,
  FEATURE_FLAGS.ANALYTICS.COMPETITIVE_ANALYSIS,
  // Collaboration - Advanced workflows
  FEATURE_FLAGS.COLLABORATION.REAL_TIME_EDITING,
  FEATURE_FLAGS.COLLABORATION.APPROVAL_WORKFLOWS,
  FEATURE_FLAGS.COLLABORATION.PROJECT_TIMELINES,
  // Security - Full security suite
  FEATURE_FLAGS.SECURITY.SSO_INTEGRATION,
  FEATURE_FLAGS.SECURITY.AUDIT_LOGGING,
  FEATURE_FLAGS.SECURITY.COMPLIANCE_REPORTING,
  FEATURE_FLAGS.SECURITY.PENETRATION_TESTING,
  // Advanced - Full advanced features
  FEATURE_FLAGS.ADVANCED.MULTI_TENANCY,
  FEATURE_FLAGS.ADVANCED.WHITE_LABEL,
  FEATURE_FLAGS.ADVANCED.API_ACCESS,
  FEATURE_FLAGS.ADVANCED.ADVANCED_PERMISSIONS,
  FEATURE_FLAGS.ADVANCED.WORKFLOW_AUTOMATION,
];

// Enterprise tier - reserved for future plans (model selection, etc.)
// No current subscription plan maps to this tier
const ENTERPRISE_FEATURES: string[] = [
  ...BUSINESS_FEATURES,
  FEATURE_FLAGS.ADVANCED.MODEL_SELECTION,
];

const TIER_FEATURE_MAP: Record<FeatureTier, string[]> = {
  [FEATURE_TIERS.MOBILE]: BASE_FEATURES,
  [FEATURE_TIERS.STARTER]: STARTER_FEATURES,
  [FEATURE_TIERS.PROFESSIONAL]: PROFESSIONAL_FEATURES,
  [FEATURE_TIERS.BUSINESS]: BUSINESS_FEATURES,
  [FEATURE_TIERS.ENTERPRISE]: ENTERPRISE_FEATURES,
};

// ============================================================================
// FEATURE CONFIGURATION - ENVIRONMENT-SPECIFIC
// ============================================================================

export const FEATURE_CONFIG = {
  // Environment-specific feature toggles
  DEVELOPMENT: {
    ENABLE_DEBUG_MODE: true,
    ENABLE_TEST_FEATURES: true,
    ENABLE_MOCK_DATA: false, // Mock data removed in favor of real implementations
    ENABLE_PERFORMANCE_MONITORING: true,
  },
  STAGING: {
    ENABLE_DEBUG_MODE: false,
    ENABLE_TEST_FEATURES: true,
    ENABLE_MOCK_DATA: false, // Mock data removed in favor of real implementations
    ENABLE_PERFORMANCE_MONITORING: true,
  },
  PRODUCTION: {
    ENABLE_DEBUG_MODE: false,
    ENABLE_TEST_FEATURES: false,
    ENABLE_MOCK_DATA: false, // Mock data removed in favor of real implementations
    ENABLE_PERFORMANCE_MONITORING: true,
  },
} as const;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Checks if a feature is enabled for a given tier
 */
export const isFeatureEnabled = (
  feature: string,
  tier: FeatureTier,
): boolean => {
  return TIER_FEATURE_MAP[tier].includes(feature);
};

/**
 * Gets all available features for a tier
 */
export const getTierFeatures = (tier: FeatureTier): string[] => {
  return TIER_FEATURE_MAP[tier];
};

/**
 * Checks if a feature is enabled in the current environment
 */
export const isFeatureEnabledInEnvironment = (feature: string): boolean => {
  const env = process.env.NODE_ENV || "development";
  const config =
    FEATURE_CONFIG[env.toUpperCase() as keyof typeof FEATURE_CONFIG];

  // Check environment-specific flags
  if (config && config[feature as keyof typeof config] !== undefined) {
    return config[feature as keyof typeof config];
  }

  // Default to enabled for development, disabled for production
  return env === "development";
};
